import React from 'react';

import { tv } from '../../tv';

/**
 * A standalone glyph: a search magnifier in an input, a calendar beside a
 * date, a lock on a private document.
 *
 * Icon exists for icons that stand on their own in product code. Inside a
 * ComplyKit component the control owns its icon: it receives the glyph
 * component as a prop (the way Checkbox renders its check and FormError its
 * alert) and sets size and colour from its own recipe, so a control's icon
 * cannot drift from the control. Passing an `<Icon>` element where a control
 * expects a glyph component fails to compile, which is what keeps the two
 * patterns from colliding on the same SVG.
 *
 * Sizes are fixed steps, never font-relative. Every reference system settled
 * there (Primer and Atlassian went as far as collapsing to a single 16px), and
 * the standard pairing puts a 16px glyph beside 14px text, so `md` is correct
 * next to body copy without any coupling to the type scale.
 *
 * `color` has no default and carries no accent role, for the same reason Text
 * has neither: status and emphasis are roles; brand colour flows from the
 * interactive or branded parent, never from a leaf primitive. An icon inside
 * an accent-coloured chip or an active button inherits that colour through
 * currentColor with no prop at all.
 *
 * Without a `label` the icon is decorative and hidden from assistive
 * technology. With one it becomes an image in its own right. An icon-only
 * button is neither case: the name belongs on the button.
 */

const iconStyles = tv({
  /* Icons sit in flex rows; without shrink-0 a tight row crushes the glyph
   * before it truncates the text beside it. The zero minimums beat the legacy
   * theme's global svg floor of 1em, which inflates a glyph set in larger text. */
  base: 'ck:shrink-0 ck:min-h-0 ck:min-w-0',
  variants: {
    size: {
      /** 12px, beside xs metadata text. */
      sm: 'ck:size-3',
      /** 16px, the standard glyph beside body copy. */
      md: 'ck:size-4',
      /** 20px. */
      lg: 'ck:size-5',
      /** 24px, for feature affordances like the document lock. */
      xl: 'ck:size-6',
    },
    color: {
      /** Primary content. */
      default: 'ck:text-text-default',
      /** Secondary: adornments, metadata glyphs. */
      muted: 'ck:text-text-muted',
      /** Something went wrong. */
      error: 'ck:text-error-text',
      /** Something succeeded. */
      success: 'ck:text-success-text',
      /** Something needs attention but has not failed. */
      warning: 'ck:text-warning-text',
    },
  },
  /* Only size defaults. A colour default would emit a class, and the class
   * would beat the enclosing region's colour; inheriting currentColor is what
   * lets a button's icon follow the button's every state for free. */
  defaultVariants: {
    size: 'md',
  },
});

export interface IconProps {
  /**
   * The glyph to render: a heroicons 24/outline component, or a custom SVG
   * component drawn in currentColor. The SVG renders directly; there is no
   * wrapper element.
   */
  as: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Fixed step: 12, 16, 20 or 24px. Icons are never font-relative. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Emphasis or status, as a role. Absent means the icon inherits the colour
   * of its surroundings, which is the common and correct case.
   */
  color?: 'default' | 'muted' | 'error' | 'success' | 'warning';
  /**
   * Names an icon that carries meaning on its own, switching it from hidden
   * decoration to an image. Name the purpose, not the picture: "Private",
   * not "Padlock". Leave it off when neighbouring text already says it, and
   * never use it to label a button: the name belongs on the button.
   */
  label?: string;
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  { as: As, size = 'md', color, label },
  ref,
) {
  return (
    <As
      ref={ref}
      className={iconStyles({ size, color })}
      /* heroicons set aria-hidden="true" on every glyph and merge our props
       * over their own, so the explicit undefined is what clears the hiding
       * when a label makes the icon meaningful. */
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      /* Legacy hardening: stops IE-era engines giving the SVG its own tab
       * stop. Inert in current browsers. */
      focusable="false"
      data-component="icon"
    />
  );
});
